import React, { useState } from "react";

/**
 * Parses a WhatsApp order message and matches products against the POS menu.
 *
 * Line format: `2x Hamburguesa Clásica (#142) - $180.00`
 * With size:   `1x Pizza Grande (#87-S23) - $220.00`
 * Followed optionally by a modifiers line, e.g.:
 *   _miel mapple | con todo_
 *   _Grande | picante | Sin cebolla, jitomate_
 * generated by ProductDetailModal.tsx as
 * `[sizeName?, ...selectedOptionNames, "con todo" | "Sin " + deselectedNames].join(" | ")`
 */
const LINE_REGEX = /(\d+)\s*x\s+.+?\(#(\d+)(?:-S(\d+))?\)/;
const NO_DESELECTION_TOKENS = ["con todo", "with everything"];
const DESELECTION_PREFIX_REGEX = /^(?:sin|no)\s+(.+)$/i;

function normalize(str) {
  return String(str)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

// Collects every option across both the legacy flat `options` list and the
// grouped `option_groups`, keyed by their (normalized) ingredient/option name.
function buildOptionNameMap(product) {
  const map = new Map();
  (product.options || []).forEach((opt) => {
    const name = opt.ingredient?.name || opt.ingredient?.name_en;
    if (name) map.set(normalize(name), opt);
  });
  (product.option_groups || []).forEach((group) => {
    (group.options || []).forEach((opt) => {
      const name = opt.ingredient?.name || opt.ingredient?.name_en;
      if (name) map.set(normalize(name), opt);
    });
  });
  return map;
}

function buildIngredientNameMap(product) {
  const map = new Map();
  (product.ingredients || []).forEach((ing) => {
    const name = ing.name || ing.name_en;
    if (name) map.set(normalize(name), ing);
  });
  return map;
}

// Parses the modifiers line into resolved option objects + deselected
// ingredient objects + any segment we couldn't match to the product (so it
// can be surfaced instead of silently dropped).
function parseModifiersLine(line, product, sizeName) {
  const segments = line
    .trim()
    .replace(/^_+|_+$/g, "") // strip WhatsApp italics markers
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);

  const optionMap = buildOptionNameMap(product);
  const ingredientMap = buildIngredientNameMap(product);
  const normalizedSizeName = sizeName ? normalize(sizeName) : null;

  const selectedOptions = [];
  const deselectedIngredients = [];
  const unresolvedSegments = [];

  segments.forEach((segment) => {
    const normalizedSegment = normalize(segment);

    if (normalizedSizeName && normalizedSegment === normalizedSizeName) {
      return; // already accounted for via the product/size id in the header line
    }

    if (NO_DESELECTION_TOKENS.includes(normalizedSegment)) {
      return; // explicit "nothing removed" marker, nothing to do
    }

    const deselectionMatch = segment.match(DESELECTION_PREFIX_REGEX);
    if (deselectionMatch) {
      const names = deselectionMatch[1].split(",").map((n) => n.trim());
      names.forEach((name) => {
        const ing = ingredientMap.get(normalize(name));
        if (ing) {
          deselectedIngredients.push(ing);
        } else {
          unresolvedSegments.push(name);
        }
      });
      return;
    }

    const opt = optionMap.get(normalizedSegment);
    if (opt) {
      selectedOptions.push(opt);
    } else {
      unresolvedSegments.push(segment);
    }
  });

  return { selectedOptions, deselectedIngredients, unresolvedSegments };
}

function parseMessage(text, menuProducts) {
  const lines = text.split("\n").filter((l) => l.trim());
  const matched = [];
  const unmatched = [];

  // Build lookup by product ID
  const productMap = {};
  menuProducts.forEach((p) => {
    productMap[p.id] = p;
  });

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const m = line.match(LINE_REGEX);
    if (!m) {
      // Skip header/footer lines (e.g. "Hola!", "Total: $X")
      if (/\d+\s*x\s+/.test(line)) {
        unmatched.push(line);
      }
      continue;
    }

    const qty = parseInt(m[1], 10);
    const productId = m[2];
    const sizeId = m[3] || null;
    const product = productMap[productId];

    if (!product) {
      unmatched.push(line);
      continue;
    }

    let price = product.price;
    let size = null;
    let needsSizeResolution = false;

    if (sizeId) {
      const sizesArr = product.sizes
        ? Array.isArray(product.sizes)
          ? product.sizes
          : Object.values(product.sizes)
        : [];
      size = sizesArr.find((s) => String(s.id) === String(sizeId));
      if (size) {
        price = size.price;
      } else {
        // Size referenced in the message no longer matches the cached menu
        // (e.g. size renamed/deleted, or stale localStorage menu_all).
        // Don't silently fall back to the product's base price — for
        // has_size products that base price is often unset/0.
        unmatched.push(line);
        continue;
      }
    } else if (
      product.has_size &&
      product.sizes &&
      (Array.isArray(product.sizes)
        ? product.sizes.length > 0
        : Object.keys(product.sizes).length > 0)
    ) {
      needsSizeResolution = true;
    }

    // The next non-empty line, if it's not itself a product line, holds the
    // modifiers (size/options/ingredients) text — consume it here.
    let selectedOptions = [];
    let deselectedIngredients = [];
    let unresolvedModifiers = [];
    const nextLine = lines[i + 1];
    if (nextLine && !LINE_REGEX.test(nextLine) && !/^\*?total/i.test(nextLine.trim())) {
      const result = parseModifiersLine(nextLine, product, size?.name);
      selectedOptions = result.selectedOptions;
      deselectedIngredients = result.deselectedIngredients;
      unresolvedModifiers = result.unresolvedSegments;
      i++; // consume the modifiers line so it isn't processed on its own
    }

    // Options carry their own price_mod (baked into the WhatsApp line total
    // the customer was quoted) — add it on top of the base/size price.
    price += selectedOptions.reduce((sum, opt) => sum + (opt.price_mod || 0), 0);

    // Merge duplicate product+size+modifier combos
    const modifierKey = [
      ...selectedOptions.map((o) => o.id).sort(),
      ...deselectedIngredients.map((ing) => ing.id).sort(),
    ].join(",");
    const key = `${productId}-${sizeId || "base"}-${modifierKey}`;
    const existing = matched.find((item) => item.key === key);
    if (existing) {
      existing.qty += qty;
    } else {
      matched.push({
        key,
        productId,
        product,
        qty,
        price,
        size,
        needsSizeResolution,
        selectedOptions,
        deselectedIngredients,
        unresolvedModifiers,
        title: product.title,
      });
    }
  }

  return { matched, unmatched };
}

const WpImportModal = ({ open, toggle, menuProducts, onImport }) => {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);

  const handleParse = () => {
    if (!text.trim()) return;
    const result = parseMessage(text, menuProducts);
    setParsed(result);
  };

  const handleImport = () => {
    if (!parsed || parsed.matched.length === 0) return;
    onImport(parsed.matched);
    setText("");
    setParsed(null);
    toggle();
  };

  const handleClose = () => {
    setText("");
    setParsed(null);
    toggle();
  };

  if (!open) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fab fa-whatsapp text-success mr-2"></i>
              Importar Pedido de WhatsApp
            </h5>
            <button
              type="button"
              className="close"
              onClick={handleClose}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {!parsed ? (
              <>
                <p className="text-muted mb-2">
                  Pega el mensaje de WhatsApp del cliente con su pedido.
                </p>
                <textarea
                  className="form-control"
                  rows={8}
                  placeholder={`Ejemplo:\n2x Hamburguesa Clásica (#142) - $180.00\n1x Pizza Grande (#87-S23) - $220.00`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  autoFocus
                />
              </>
            ) : (
              <>
                {parsed.matched.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-success">
                      <i className="fas fa-check-circle mr-1"></i>
                      Productos encontrados ({parsed.matched.length})
                    </h6>
                    <table className="table table-sm table-bordered mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>Cant.</th>
                          <th>Producto</th>
                          <th>Tamaño</th>
                          <th>Modificadores</th>
                          <th className="text-right">Precio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.matched.map((item) => (
                          <tr key={item.key}>
                            <td>{item.qty}</td>
                            <td>
                              {item.title}
                              {item.needsSizeResolution && (
                                <span className="badge badge-warning ml-1">
                                  Sin tamaño
                                </span>
                              )}
                            </td>
                            <td>{item.size ? item.size.name : "—"}</td>
                            <td>
                              {item.selectedOptions.length === 0 &&
                              item.deselectedIngredients.length === 0 ? (
                                "—"
                              ) : (
                                <>
                                  {item.selectedOptions
                                    .map((o) => o.ingredient?.name)
                                    .filter(Boolean)
                                    .join(", ")}
                                  {item.selectedOptions.length > 0 &&
                                    item.deselectedIngredients.length > 0 &&
                                    " | "}
                                  {item.deselectedIngredients.length > 0 &&
                                    "Sin " +
                                      item.deselectedIngredients
                                        .map((ing) => ing.name)
                                        .join(", ")}
                                </>
                              )}
                              {item.unresolvedModifiers.length > 0 && (
                                <span
                                  className="badge badge-warning ml-1"
                                  title={item.unresolvedModifiers.join(", ")}
                                >
                                  Revisar: {item.unresolvedModifiers.join(", ")}
                                </span>
                              )}
                            </td>
                            <td className="text-right">
                              ${(item.price * item.qty).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="font-weight-bold">
                          <td colSpan="4" className="text-right">
                            Total:
                          </td>
                          <td className="text-right">
                            $
                            {parsed.matched
                              .reduce(
                                (sum, item) => sum + item.price * item.qty,
                                0
                              )
                              .toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                {parsed.unmatched.length > 0 && (
                  <div>
                    <h6 className="text-danger">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      No encontrados ({parsed.unmatched.length})
                    </h6>
                    {parsed.unmatched.map((line, i) => (
                      <p
                        key={i}
                        className="mb-1 small text-danger"
                        style={{ fontFamily: "monospace" }}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                )}
                {parsed.matched.length === 0 && parsed.unmatched.length === 0 && (
                  <p className="text-muted">
                    No se encontraron productos en el mensaje. Asegúrate de que
                    el mensaje fue generado desde el menú de Foo.
                  </p>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            {!parsed ? (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={handleClose}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleParse}
                  disabled={!text.trim()}
                >
                  <i className="fas fa-search mr-1"></i>
                  Importar
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => setParsed(null)}
                >
                  <i className="fas fa-arrow-left mr-1"></i>
                  Volver
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleImport}
                  disabled={parsed.matched.length === 0}
                >
                  <i className="fas fa-cart-plus mr-1"></i>
                  Agregar al pedido ({parsed.matched.length})
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WpImportModal;
